/**
 * Inventory API Service
 * 
 * Handles ONLY: Count sessions, stock transfers, bulk operations, templates, branch settings.
 * Stock levels, adjustments, and confirmation are in stockApi.ts.
 */

import apiClient from '@/lib/api-client';

// ============================================================
// TYPES
// ============================================================

export interface CountSession {
    id: string; tenantId: string; branchId: string; branchName?: string;
    countType: 'full' | 'cycle'; status: 'draft' | 'in_progress' | 'completed' | 'cancelled';
    countedBy: string | null; countedByName?: string; approvedBy: string | null; approvedByName?: string;
    totalItems: number; countedItems: number; varianceItems: number;
    notes: string | null; startedAt: string | null; completedAt: string | null;
    createdAt: string; updatedAt: string;
}

export interface CountItem {
    id: string; stockCountId: string; productId: string; productName?: string;
    variantId: string | null; variantName?: string | null;
    expectedQuantity: string; countedQuantity: string | null; variance: string | null; notes: string | null;
}

export interface CountDetail extends CountSession { items: CountItem[]; }

export interface VarianceReport {
    countId: string; branchId: string; branchName?: string; countType: 'full' | 'cycle';
    completedAt: string | null; totalItems: number; countedItems: number;
    exactMatchItems: number; varianceItems: number; overCountItems: number; underCountItems: number;
    totalExpectedQuantity: string; totalCountedQuantity: string; totalVariance: string;
    items: VarianceReportItem[];
}

export interface VarianceReportItem {
    productId: string; productName?: string; variantId: string | null; variantName?: string | null;
    expectedQuantity: string; countedQuantity: string; variance: string; variancePercentage: string; notes: string | null;
}

export interface TransferSummary {
    id: string; tenantId: string; fromBranchId: string; fromBranchName?: string;
    toBranchId: string; toBranchName?: string;
    status: 'pending' | 'approved' | 'in_transit' | 'received' | 'cancelled';
    requestedBy: string; requesterName?: string; approvedBy: string | null; receivedBy: string | null;
    totalItems: number; totalQuantity: number; notes: string | null;
    createdAt: string; updatedAt: string;
}

export interface TransferItem {
    id: string; transferId: string; productId: string; productName?: string;
    variantId: string | null; variantName?: string | null; quantity: string;
}

export interface TransferDetail extends TransferSummary { items: TransferItem[]; }

export interface BranchStockSettings {
    branchId: string; branchName?: string; autoConfirmStock: boolean; requirePhysicalCount: boolean;
}

export interface BulkCountResult {
    uploadId: string; totalRows: number; successCount: number; errorCount: number;
    skippedCount: number; errors: BulkUploadError[]; hasErrors: boolean;
}

export interface BulkTransferResult {
    uploadId: string; totalRows: number; successCount: number; errorCount: number;
    skippedCount: number; errors: BulkUploadError[]; hasErrors: boolean;
    transferId?: string; totalQuantityTransferred: number;
}

export interface BulkUploadError {
    row: number; productName: string; field: string; message: string; suggestedFix?: string;
}

// ============================================================
// COUNT SESSIONS
// ============================================================

export async function getCountSessions(tenantId: string, params: { limit?: number; status?: string; branchId?: string } = {}): Promise<CountSession[]> {
    const response = await apiClient.get(`/tenants/${tenantId}/inventory/counts`, { params });
    if (response.data?.data && Array.isArray(response.data.data)) return response.data.data;
    if (Array.isArray(response.data)) return response.data;
    return [];
}

export async function getCountSession(
    tenantId: string,
    countId: string,
    page?: number,
    limit?: number,
    search?: string,
    filter?: string
): Promise<CountDetail & { pagination?: any }> {
    const params: Record<string, any> = {};
    if (page) params.page = page;
    if (limit) params.limit = limit;
    if (search) params.search = search;
    if (filter && filter !== 'all') params.filter = filter;

    const response = await apiClient.get(
        `/tenants/${tenantId}/inventory/counts/${countId}`,
        { params }
    );
    return response.data?.data || response.data;
}

export async function createCountSession(tenantId: string, data: { branchId: string; countType: 'full' | 'cycle'; productIds?: string[]; notes?: string }): Promise<CountDetail> {
    const response = await apiClient.post(`/tenants/${tenantId}/inventory/counts`, { ...data, branchId: data.branchId || '00000000-0000-0000-0000-000000000000' });
    return response.data?.data || response.data;
}

export async function updateCountItem(tenantId: string, countId: string, itemId: string, data: { countedQuantity: string; notes?: string }): Promise<void> {
    await apiClient.patch(`/tenants/${tenantId}/inventory/counts/${countId}/items/${itemId}`, data);
}

export async function completeCountSession(tenantId: string, countId: string): Promise<VarianceReport> {
    const response = await apiClient.post(`/tenants/${tenantId}/inventory/counts/${countId}/complete`);
    return response.data?.data || response.data;
}

export async function cancelCountSession(tenantId: string, countId: string): Promise<void> {
    await apiClient.post(`/tenants/${tenantId}/inventory/counts/${countId}/cancel`);
}

export async function getVarianceReport(tenantId: string, countId: string): Promise<VarianceReport> {
    const response = await apiClient.get(`/tenants/${tenantId}/inventory/counts/${countId}/variance-report`);
    return response.data?.data || response.data;
}

// ============================================================
// TRANSFERS
// ============================================================

export async function getTransfers(tenantId: string, params: { limit?: number; status?: string; branchId?: string } = {}): Promise<TransferSummary[]> {
    const response = await apiClient.get(`/tenants/${tenantId}/inventory/transfers`, { params });
    if (response.data?.data && Array.isArray(response.data.data)) return response.data.data;
    if (Array.isArray(response.data)) return response.data;
    return [];
}

export async function getTransfer(tenantId: string, transferId: string): Promise<TransferDetail> {
    const response = await apiClient.get(`/tenants/${tenantId}/inventory/transfers/${transferId}`);
    return response.data?.data || response.data;
}

export async function createTransfer(tenantId: string, data: { fromBranchId: string; toBranchId: string; items: Array<{ productId: string; variantId?: string; quantity: string }>; notes?: string }): Promise<TransferDetail> {
    const response = await apiClient.post(`/tenants/${tenantId}/inventory/transfers`, data);
    return response.data?.data || response.data;
}

export async function approveTransfer(tenantId: string, transferId: string): Promise<TransferDetail> {
    const response = await apiClient.post(`/tenants/${tenantId}/inventory/transfers/${transferId}/approve`);
    return response.data?.data || response.data;
}

export async function receiveTransfer(tenantId: string, transferId: string): Promise<TransferDetail> {
    const response = await apiClient.post(`/tenants/${tenantId}/inventory/transfers/${transferId}/receive`);
    return response.data?.data || response.data;
}

export async function cancelTransfer(tenantId: string, transferId: string): Promise<TransferDetail> {
    const response = await apiClient.post(`/tenants/${tenantId}/inventory/transfers/${transferId}/cancel`);
    return response.data?.data || response.data;
}

// ============================================================
// BULK OPERATIONS
// ============================================================

export async function bulkUploadCount(tenantId: string, branchId: string, file: File): Promise<BulkCountResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('branchId', branchId || '00000000-0000-0000-0000-000000000000');
    const response = await apiClient.post(`/tenants/${tenantId}/inventory/counts/bulk-upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data?.data || response.data;
}

export async function bulkUploadTransfer(tenantId: string, fromBranchId: string, toBranchId: string, file: File): Promise<BulkTransferResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fromBranchId', fromBranchId);
    formData.append('toBranchId', toBranchId);
    const response = await apiClient.post(`/tenants/${tenantId}/inventory/transfers/bulk`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data?.data || response.data;
}

// ============================================================
// TEMPLATES
// ============================================================

export async function downloadCountTemplate(tenantId: string, branchId?: string): Promise<Blob> {
    const params: Record<string, string> = {};
    if (branchId) params.branchId = branchId;
    const response = await apiClient.get(`/tenants/${tenantId}/inventory/templates/count-sheet`, { params, responseType: 'blob' });
    return response.data;
}

export async function downloadCountTemplateAdvanced(tenantId: string, branchId?: string): Promise<Blob> {
    const params: Record<string, string> = {};
    if (branchId) params.branchId = branchId;
    const response = await apiClient.get(`/tenants/${tenantId}/inventory/templates/count-sheet-advanced`, { params, responseType: 'blob' });
    return response.data;
}

export async function downloadTransferTemplate(tenantId: string): Promise<Blob> {
    const response = await apiClient.get(`/tenants/${tenantId}/inventory/templates/transfer-sheet`, { responseType: 'blob' });
    return response.data;
}

export async function downloadBulkUploadErrorReport(tenantId: string, uploadId: string): Promise<Blob> {
    const response = await apiClient.get(`/tenants/${tenantId}/inventory/bulk-upload/${uploadId}/errors`, { responseType: 'blob' });
    return response.data;
}

// ============================================================
// BRANCH SETTINGS
// ============================================================

export async function getBranchStockSettings(tenantId: string, branchId: string): Promise<BranchStockSettings> {
    const response = await apiClient.get(`/tenants/${tenantId}/inventory/settings/${branchId}`);
    return response.data?.data || response.data;
}

export async function updateBranchStockSettings(tenantId: string, branchId: string, data: { autoConfirmStock?: boolean; requirePhysicalCount?: boolean }): Promise<BranchStockSettings> {
    const response = await apiClient.patch(`/tenants/${tenantId}/inventory/settings/${branchId}`, data);
    return response.data?.data || response.data;
}